import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  Smartphone,
  Key,
  RefreshCw,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthService from '../../services/authService';

const MFARecoveryVerify = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState('');
  const [selectedAction, setSelectedAction] = useState('remove_trusted_devices');

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);


  const verifyToken = async () => {
    try {
      setLoading(true);
      const data = await AuthService.verifyMFARecoveryToken(token);
      setTokenData(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired recovery link');
    } finally {
      setLoading(false);
    }
  };

  const executeRecovery = async () => {
    try {
      setSubmitting(true);
      const response = await AuthService.executeMFARecovery(token, selectedAction);
      
      toast.success('Account recovery completed successfully!');
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: response.detail || 'Account recovery completed successfully!',
            type: 'success'
          }
        });
      }, 2000);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recovery failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const recoveryOptions = [
    {
      id: 'remove_trusted_devices',
      title: 'Remove Trusted Devices',
      subtitle: 'Recommended',
      description: 'Remove all trusted devices. You\'ll need to verify MFA again the next time you log in from any device.',
      icon: Smartphone,
      recommended: true
    },
    {
      id: 'reset_mfa',
      title: 'Reset MFA Completely',
      subtitle: 'Complete reset',
      description: 'Remove all MFA methods and disable MFA entirely. You can set up new MFA methods later.',
      icon: RefreshCw,
      recommended: false
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-emerald-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 w-full max-w-md text-center"
        >
          <Loader2 className="w-8 h-8 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verifying recovery link...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 w-full max-w-md text-center"
        >
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Invalid Recovery Link</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/mfa/recovery/request')}
            className="w-full bg-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-red-700 transition-colors"
          >
            Request New Recovery Link
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-emerald-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 w-full max-w-2xl"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 150 }}
            className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <Shield className="w-8 h-8 text-primary-600" />
          </motion.div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">MFA Account Recovery</h1>
          <p className="text-gray-600">Choose how you'd like to recover access to your account</p>
        </div>

        {/* Account Info */}
        <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between text-sm">
            <div>
              <p className="font-medium text-primary-900">Account: {tokenData?.user_email || 'Loading...'}</p>
              <p className="text-primary-700">Recovery Link: Valid</p>
            </div>
            <div className="text-right">
              <p className="text-primary-700 flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                Expires: {tokenData?.expires_at ? new Date(tokenData.expires_at).toLocaleString() : 'Unknown'}
              </p>
            </div>
          </div>
        </div>

        {/* Current MFA Status */}
        {tokenData?.current_mfa_methods?.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Current MFA Methods</h3>
            <div className="space-y-2">
              {tokenData.current_mfa_methods.map((method, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{method.device_name || method.type}</span>
                  {method.is_primary && (
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">Primary</span>
                  )}
                </div>
              ))}
              {tokenData.trusted_devices_count > 0 && (
                <p className="text-gray-600 text-sm mt-2">
                  {tokenData.trusted_devices_count} trusted device(s)
                </p>
              )}
            </div>
          </div>
        )}

        {/* Recovery Options */}
        <div className="space-y-4 mb-8">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Choose Recovery Action</h3>
          {recoveryOptions.map((option) => {
            const IconComponent = option.icon;
            return (
              <motion.div
                key={option.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  selectedAction === option.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onClick={() => setSelectedAction(option.id)}
              >
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${
                    selectedAction === option.id ? 'bg-primary-100' : 'bg-gray-100'
                  }`}>
                    <IconComponent className={`w-5 h-5 ${
                      selectedAction === option.id ? 'text-primary-600' : 'text-gray-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">{option.title}</h4>
                      {option.recommended && (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                          {option.subtitle}
                        </span>
                      )}
                      {!option.recommended && (
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs">
                          {option.subtitle}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm">{option.description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedAction === option.id
                      ? 'border-primary-500 bg-primary-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedAction === option.id && (
                      <CheckCircle className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Security Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-900 mb-1">Security Notice</h4>
              <p className="text-amber-800 text-sm">
                After recovery, if MFA is required for your account, you'll need to set up MFA again to maintain account security. 
                This action cannot be undone.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center space-x-2 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Login</span>
          </button>
          <button
            onClick={executeRecovery}
            disabled={submitting}
            className="flex-1 bg-primary-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>Complete Recovery</span>
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default MFARecoveryVerify;
